import { type VolumeBasedPriceV1 } from '@redteclab/api/clients/bully'

import {
  useGetDiscountPercentages,
  useGetDiscountRanges,
} from 'views/components/organisms/VolumePricingModal/utils/volumePricingAccessor'

type UseGenerateVolumePricingBadgeDataProps = {
  discount?: number
  discountActivated?: boolean
  quantity: number
}

export const calculateDiscount = (
  basePrice?: number,
  newPrice?: number
): number | undefined => {
  if (!basePrice || !newPrice) {
    return undefined
  }
  const discount = Math.floor(((basePrice - newPrice) / basePrice) * 100)
  if (discount > 0) {
    return discount
  }

  return undefined
}

/**
 * Gets range object based on the selected quantity, so it can later be used to get:
 * 1. @param currentRangePrice used after first discount threshold is achieved, so success is displayed in the label (ex. 6% Mengenrabatt aktiviert)
 * 2. @param nextRangePrice: used when the quantity is lower than first threshold, so we can display information about first threshold and how to achieve it (ex. ab 3 Stuck 6% sparen)
 */
export const useGenerateVolumePricingBadgeData = (
  quantity: number,
  volumePricing: VolumeBasedPriceV1[]
): UseGenerateVolumePricingBadgeDataProps => {
  const ranges = useGetDiscountRanges(volumePricing) ?? []

  const { currentDiscountPercentage, firstDiscountPercentage } =
    useGetDiscountPercentages(ranges, quantity)

  const firstMinTreshold = ranges[1]?.rangeStart ?? 0

  if (quantity < firstMinTreshold) {
    return {
      discount: firstDiscountPercentage,
      discountActivated: false,
      quantity: firstMinTreshold,
    }
  }

  return {
    discount: currentDiscountPercentage,
    discountActivated: true,
    quantity,
  }
}
